import { StringUtils, engineComponent } from "neo-common";
import { ComponentTypes } from "../../ComponentTypes";
import { ImageElement } from "./ImageElement";
import { StageController } from "../../StageController";
import { BaseElement } from "./BaseElement";
import { Tweens } from "../../Effects/Tweens";
import { StyleManager } from "../../Managers/StyleManager";
import { ParticleStyle } from "../Styles/ParticleStyle";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../../Utils/FrameTimer";
import { CustomUtils } from "../../../Utils/CustomUtils";
import { AnimationElement } from "./AnimationElement";
import Animation = gsap.Animation;

// TODO add comments
@engineComponent(ComponentTypes.DisplayObject)
export class ParticleElement extends BaseElement {
    // handle definition for particles
    private _style: ParticleStyle;
    private _styleName: string;

    //  holds particles in pool and reused them
    private _pool: Array<ImageElement | AnimationElement>;

    // container of whole particle element
    private _target: BaseElement;
    // timer for add particles
    private _timer: FrameTimerEventEmitter;

    private _stopping: boolean;
    private _stop: boolean;
    private _emitOnce: boolean;
    private _preGenerate: boolean;
    private _startType: string;
    private _randomStartFrame: boolean;
    private _maxEmit: number;

    private _blendMode: number;
    private _created: boolean;

    constructor() {
        super();

        this._pool = [];

        this._timer = CreateFrameTimer("particleTimer");
        this._timer.on(FrameTimerEvents.Complete, this.addParticles.bind(this));

        this._blendMode = 0;
        this._created = false;
        this._stopping = false;
        this._stop = true;
        this._emitOnce = false;
        this._preGenerate = false;
        this._startType = "";
        this._randomStartFrame = false;
        this._maxEmit = 1;
        this.visible = false;
        this._target = this;
    }

    //******************************************************************************************************************
    //  INITIALIZATION
    //******************************************************************************************************************

    createByNode(node: Element): void {
        if (!this._created) {
            super.createByNode(node);

            //prevent generating wasted amount of particles, emit once can be setup only on create
            if (node.hasAttribute(ParticleElementAttributes.EMIT_ONCE)) {
                this._emitOnce = StringUtils.parseBoolean(node.getAttribute(ParticleElementAttributes.EMIT_ONCE));
            }

            if (node.hasAttribute(ParticleElementAttributes.PRE_GENERATE)) {
                this._preGenerate = StringUtils.parseBoolean(node.getAttribute(ParticleElementAttributes.PRE_GENERATE));
            }

            if (node.hasAttribute(ParticleElementAttributes.BLEND_MODE)) {
                this._blendMode = parseInt(node.getAttribute(ParticleElementAttributes.BLEND_MODE));
            }

            if (node.hasAttribute(ParticleElementAttributes.RANDOM_START_FRAME)) {
                this._randomStartFrame = StringUtils.parseBoolean(
                    node.getAttribute(ParticleElementAttributes.RANDOM_START_FRAME),
                );
            }

            if (
                node.hasAttribute(ParticleElementAttributes.STYLE) ||
                node.hasAttribute(ParticleElementAttributes.STYLE2)
            ) {
                if (node.hasAttribute(ParticleElementAttributes.STYLE)) {
                    this._style = <ParticleStyle>(
                        StyleManager.Styles[node.getAttribute(ParticleElementAttributes.STYLE)]
                    );
                } else {
                    this._style = <ParticleStyle>(
                        StyleManager.Styles[node.getAttribute(ParticleElementAttributes.STYLE2)]
                    );
                }

                this._styleName = this._style.name;
            }

            //if is subParticle, create image representation of particle, otherwise create just child particles
            this.createParticles();
            this._created = true;
        }
    }

    updateByNode(node: Element): void {
        if (DEBUG) {
            if (
                node.hasAttribute(ParticleElementAttributes.DEBUG) &&
                node.getAttribute(ParticleElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        super.updateByNode(node);

        if (node.hasAttribute(ParticleElementAttributes.MAX_EMIT)) {
            this._maxEmit = parseInt(node.getAttribute(ParticleElementAttributes.MAX_EMIT));
            this._maxEmit = this._maxEmit * 0.01;

            if (this._maxEmit > 1) {
                this._maxEmit = 1;
            }
        }

        if (node.hasAttribute(ParticleElementAttributes.START_TYPE)) {
            this._startType = node.getAttribute(ParticleElementAttributes.START_TYPE);
        }

        if (
            node.hasAttribute(ParticleElementAttributes.START) &&
            StringUtils.parseBoolean(node.getAttribute(ParticleElementAttributes.START))
        ) {
            this.start(this._emitOnce, this._startType);
        }

        if (
            node.hasAttribute(ParticleElementAttributes.STOPPING) &&
            StringUtils.parseBoolean(node.getAttribute(ParticleElementAttributes.STOPPING))
        ) {
            this.stopping();
        }

        if (
            node.hasAttribute(ParticleElementAttributes.STOP) &&
            StringUtils.parseBoolean(node.getAttribute(ParticleElementAttributes.STOP))
        ) {
            this.stop();
        }

        if (
            node.hasAttribute(ParticleElementAttributes.RESET) &&
            StringUtils.parseBoolean(node.getAttribute(ParticleElementAttributes.RESET))
        ) {
            this.reset();
        }

        if (
            node.hasAttribute(ParticleElementAttributes.CLEAR) &&
            StringUtils.parseBoolean(node.getAttribute(ParticleElementAttributes.CLEAR))
        ) {
            this.clear();
        }
    }

    //******************************************************************************************************************
    //  CREATING PARTICLES
    //******************************************************************************************************************

    public createParticles(): void {
        if (this._style != null) {
            if (this._style.images != null && this._style.images.length > 0) {
                let count: number = this._emitOnce ? this._style.count : this._style.calculateElements();

                for (let i: number = 0; i < count; i++) {
                    let particle: ImageElement | AnimationElement = this.createParticle();

                    this._pool.push(particle);
                }
            }
        }
    }

    private createParticle(): ImageElement | AnimationElement {
        let particle: ImageElement | AnimationElement;
        particle = this.createParticleResource();

        this.setupParticle(particle);

        if (this._preGenerate) {
            this.setupPosition(particle);
            this.addBehaviour(particle);
        }

        return particle;
    }

    private createParticleResource(): ImageElement | AnimationElement {
        if (this._style != null) {
            let resource: ImageElement | AnimationElement = null;
            let resourceId = this.getRandomResource();

            if (resourceId.indexOf("GFX_") > -1) {
                resource = new ImageElement(StageController.getTextureByResourceId(resourceId));
                resource.blendMode = CustomUtils.getBlendMode(this._blendMode);
            } else if (resourceId.indexOf("ANI_") > -1) {
                let node: Element = document.createElement("Animation");
                node.setAttribute("Style2", resourceId);
                node.setAttribute("RandomStartFrame", this._randomStartFrame.toString());
                resource = new AnimationElement();
                resource.create(node);
            }

            let localBounds = resource.getLocalBounds();
            resource.pivot.set(localBounds.width / 2, localBounds.height / 2);
            return resource;
        }

        return null;
    }

    private getRandomResource(): string {
        return this._style.images[Math.floor(Math.random() * this._style.images.length)];
    }

    //******************************************************************************************************************
    //  INITIAL SETUP
    //******************************************************************************************************************

    private setupParticle(particle: ImageElement | AnimationElement) {
        this.setVisibility(particle, false);
        particle.usedInPool = false;

        return particle;
    }

    private setVisibility(particle: ImageElement | AnimationElement, visible: boolean): void {
        particle.visible = visible;

        if (particle instanceof AnimationElement) {
            if (visible) {
                particle.playMe();
            } else {
                particle.stop();
            }
        }
    }

    private setupPosition(particle: ImageElement | AnimationElement): void {
        let startX = 0;
        let startY = 0;

        switch (this._style.positionZone.type) {
            case ParticleStyle.ZoneRectangle:
                startX = this._style.positionZone.x + Math.random() * this._style.positionZone.width;
                startY = this._style.positionZone.y + Math.random() * this._style.positionZone.height;

                if (this._style.positionZone.innerWidth != -1 && this._style.positionZone.innerHeight != -1) {
                    let centerX = this._style.positionZone.x + this._style.positionZone.width / 2;
                    let centerY = this._style.positionZone.y + this._style.positionZone.height / 2;
                    let innerCenterX = this._style.positionZone.innerX + this._style.positionZone.innerWidth / 2;
                    let innerCenterY = this._style.positionZone.innerY + this._style.positionZone.innerHeight / 2;
                    let correctX = Math.random() > 0.5;

                    if (correctX) {
                        if (startX > centerX) {
                            startX = this.getRandom(
                                innerCenterX + this._style.positionZone.innerWidth * 0.5,
                                centerX + this._style.positionZone.width * 0.5,
                            );
                        } else {
                            startX = this.getRandom(0, innerCenterX - this._style.positionZone.innerWidth * 0.5);
                        }
                    } else {
                        if (startY > centerY) {
                            startY = this.getRandom(
                                innerCenterY + this._style.positionZone.innerHeight * 0.5,
                                centerY + this._style.positionZone.height * 0.5,
                            );
                        } else {
                            startY = this.getRandom(0, innerCenterY - this._style.positionZone.innerHeight * 0.5);
                        }
                    }
                }

                particle.x = startX;
                particle.y = startY;

                break;
            case ParticleStyle.ZoneTarget:
                startX =
                    this._target.x +
                    Math.random() * this._style.positionZone.width -
                    this._style.positionZone.width / 2;
                startY =
                    this._target.y +
                    Math.random() * this._style.positionZone.height -
                    this._style.positionZone.height / 2;

                particle.x = startX;
                particle.y = startY;
                break;
            case ParticleStyle.ZoneLine:
                // TODO
                break;
            case ParticleStyle.ZoneEllipse:
                startX = this._style.positionZone.x;
                startY = this._style.positionZone.y;
                let radianDegrees = Math.floor(
                    this.getRandom(this._style.positionZone.startAngle, this._style.positionZone.angle),
                );
                let radian = this.degrees2radians(radianDegrees);

                //start is not from origin but from another, inner circle
                if (this._style.positionZone.innerRadius > 0) {
                    let radius = this.getRandom(
                        this._style.positionZone.minInnerRadius,
                        this._style.positionZone.innerRadius,
                    );
                    let radiusB = this.getRandom(
                        this._style.positionZone.minInnerRadiusB,
                        this._style.positionZone.innerRadiusB,
                    );

                    startX = Math.floor(this._style.positionZone.innerX + radius * Math.cos(radian));
                    startY = Math.floor(this._style.positionZone.innerY + radiusB * Math.sin(radian));
                }

                particle.x = startX;
                particle.y = startY;
                particle.rotation = radian;

                break;
        }
    }

    public setTargetElement(element: BaseElement): void {
        this._target = element;
    }

    //******************************************************************************************************************
    //  BEHAVIOUR
    //******************************************************************************************************************

    private addBehaviour(particle: ImageElement | AnimationElement): void {
        let particleLife: number =
            Math.floor(Math.random() * (this._style.maxLife - this._style.minLife)) + this._style.minLife;

        this.addPathBehaviour(particle, particleLife);
        this.addScaleBehaviour(particle, particleLife);
        this.addRotationBehaviour(particle, particleLife);
        this.addAlphaBehaviour(particle, particleLife);
    }

    private addPathBehaviour(particle: ImageElement | AnimationElement, life: number): void {
        switch (this._style.positionZone.type) {
            case ParticleStyle.ZoneRectangle:
            case ParticleStyle.ZoneTarget:
                this.addRectanglePathBehavior(particle, life);
                break;
            case ParticleStyle.ZoneEllipse:
                this.addEllipsePathBehavior(particle, life);
                break;
        }
    }

    private addRectanglePathBehavior(particle: ImageElement | AnimationElement, life: number): void {
        if (this._style.pathValues != null) {
            let points: Array<Object> = [];
            for (let i: number = 0; i < this._style.pathValues.length; i++) {
                if (
                    this._style.pathValues[i] != null &&
                    this._style.pathValues[i].x != null &&
                    this._style.pathValues[i].y != null
                ) {
                    let valueX: number = this._style.pathValues[i].x;
                    let valueY: number = this._style.pathValues[i].y;
                    if (this._style.pathValues[i].randomX != null) {
                        valueX =
                            Math.floor(
                                Math.random() * (this._style.pathValues[i].randomX - this._style.pathValues[i].x),
                            ) + this._style.pathValues[i].x;
                    }
                    if (this._style.pathValues[i].randomY != null) {
                        valueY =
                            Math.floor(
                                Math.random() * (this._style.pathValues[i].randomY - this._style.pathValues[i].y),
                            ) + this._style.pathValues[i].y;
                    }
                    points.push({ x: particle.x + valueX, y: particle.y + valueY });
                }
            }
            if (points.length > 2) {
                Tweens.positionTween(particle, {
                    bezier: {
                        values: points,
                    },
                    tweenX: [particle.x, particle.x],
                    tweenY: [particle.y, particle.y],
                    tweenDuration: life,
                    paused: false,
                    callbackFunction: this.removeFromPool,
                    callbackParams: [particle],
                    callbackScope: null,
                });
            } else if (points.length === 2) {
                Tweens.positionTween(particle, {
                    tweenX: [points[0]["x"], points[1]["x"]],
                    tweenY: [points[0]["y"], points[1]["y"]],
                    tweenDuration: life,
                    paused: false,
                    callbackFunction: this.removeFromPool,
                    callbackParams: [particle],
                    callbackScope: null,
                });
            }
        }
    }

    private addEllipsePathBehavior(particle: ImageElement | AnimationElement, life: number): void {
        let radius = this.getRandom(this._style.positionZone.minRadius, this._style.positionZone.radius);
        let radiusB = this.getRandom(this._style.positionZone.minRadiusB, this._style.positionZone.radiusB);
        let finalX = Math.floor(this._style.positionZone.x + radius * Math.cos(particle.rotation));
        let finalY = Math.floor(this._style.positionZone.y + radiusB * Math.sin(particle.rotation));

        Tweens.positionTween(particle, {
            tweenX: [particle.x, finalX],
            tweenY: [particle.y, finalY],
            tweenDuration: life,
            paused: false,
            callbackFunction: this.removeFromPool,
            callbackParams: [particle],
            callbackScope: null,
        });
    }

    private addScaleBehaviour(particle: ImageElement | AnimationElement, life: number): void {
        if (this._style.scaleValues != null) {
            let scalePoints: Array<PIXI.Point> = [];
            for (let i: number = 0; i < this._style.scaleValues.length; i++) {
                let scaleValue: number;
                if (this._style.scaleValues[i].length > 1) {
                    scaleValue =
                        Math.random() * (this._style.scaleValues[i][1] - this._style.scaleValues[i][0]) +
                        this._style.scaleValues[i][0];
                } else {
                    scaleValue = this._style.scaleValues[i][0];
                }

                scalePoints.push(new PIXI.Point(scaleValue, scaleValue));
            }

            if (scalePoints.length === 1) {
                particle.scale.x = scalePoints[0].x;
                particle.scale.y = scalePoints[0].y;
            } else if (scalePoints.length > 1) {
                Tweens.scaleTween(particle, {
                    tweenValues: scalePoints,
                    tweenDuration: life,
                    paused: false,
                });
            }
        }
    }

    private addRotationBehaviour(particle: ImageElement | AnimationElement, life: number): void {
        if (this._style.rotationValues != null && this._style.rotationValues.length > 1) {
            Tweens.rotationTween(particle, {
                degrees: [this._style.rotationValues[0], this._style.rotationValues[1]],
                tweenDuration: life,
                paused: false,
            });
        }
    }

    private addAlphaBehaviour(particle: ImageElement | AnimationElement, life: number): void {
        if (this._style.alphaValues != null) {
            let alphas: Array<number> = [];
            for (let i: number = 0; i < this._style.alphaValues.length; i++) {
                alphas.push(this._style.alphaValues[i]);
            }
            Tweens.alphaTween(particle, {
                alphas: alphas,
                tweenDuration: life,
                paused: false,
            });
        }
    }

    //******************************************************************************************************************
    //  GETTING PARTICLES
    //******************************************************************************************************************

    private addParticles(): void {
        if (!this._stop) {
            let tabActive = true;
            if (document.visibilityState != null && document.visibilityState === "hidden") {
                tabActive = false;
            }

            if (tabActive) {
                this.getFromPool();
            }

            if (!this._stopping) {
                this._timer.start(this._style.time);
            }
        }
    }

    private getFreeParticleIndex(): number {
        for (let i: number = 0; i < this._pool.length; i++) {
            if (!this._pool[i].usedInPool) {
                return i;
            }
        }

        return -1;
    }

    private getFromPool(): void {
        let count = Math.floor(this._style.count * this._maxEmit);

        for (let i: number = 0; i < count; i++) {
            let particle: ImageElement | AnimationElement;
            let index: number = this.getFreeParticleIndex();

            // to keep pool ordered by usedInPool check if first element is usedInPool
            // if yes, create new and push to pool
            // if not, shift element, set usedInPool and push to pool
            if (index == -1) {
                particle = this.createParticle();

                this._pool.push(particle);
            } else {
                particle = this._pool[index];
            }

            if (particle != null) {
                particle.usedInPool = true;
                this.addChild(particle);
                this.setVisibility(particle, true);

                if (this._preGenerate) {
                    Tweens.restartTweens(particle.uid);
                } else {
                    Tweens.removeTweens(particle);
                    this.setupPosition(particle);
                    this.addBehaviour(particle);
                }
            }
        }
    }

    //******************************************************************************************************************
    //  CONTROLS
    //******************************************************************************************************************

    start(emitOnce: boolean = false, startType: string = ""): void {
        this.visible = true;

        //if there is no style, there is no particles to emit
        if (this._style != null) {
            this._timer.stop();
            this._emitOnce = emitOnce;
            this._startType = startType;
            this._stopping = emitOnce;
            this._stop = false;
            if (!this._emitOnce) {
                this._timer.start(this._style.time);
                if (this._startType === "now") {
                    this.addParticles();
                }
            } else {
                if (this._startType === "now") {
                    this.addParticles();
                } else {
                    this._timer.start(this._style.time);
                }
            }
        }
    }

    stopping(): void {
        this._stopping = true;
    }

    stop(): void {
        this._stopping = false;
        this._stop = true;
        this._timer.stop();
        this.visible = false;
        this.removeFromStage();
    }

    reset(): void {
        for (let i: number = 0; i < this._pool.length; i++) {
            this.setupPosition(this._pool[i]);
            this.setVisibility(this._pool[i], false);
        }
    }

    clear(): void {
        this._stopping = false;
        this._stop = true;
        this._timer.stop();
        this.visible = false;
        this.removeAllFromPool();
    }

    //******************************************************************************************************************
    //  REMOVING
    //******************************************************************************************************************

    private removeFromPool(particle: ImageElement | AnimationElement): void {
        // this.setVisibility(particle, false);
        particle.visible = false;
        if (particle instanceof AnimationElement) {
            particle.stop();
        }

        particle.usedInPool = false;
    }

    private removeFromStage(): void {
        for (let i: number = 0; i < this._pool.length; i++) {
            this.setVisibility(this._pool[i], false);
            this.removeChild(this._pool[i]);
            this._pool[i].usedInPool = false;
        }
    }

    private removeAllFromPool(): void {
        for (let i: number = 0; i < this._pool.length; i++) {
            this.setVisibility(this._pool[i], false);
            this.removeChild(this._pool[i]);
            Tweens.removeTweens(this._pool[i]);
            this._pool[i].usedInPool = false;
        }
    }

    //******************************************************************************************************************
    //  HELPER
    //******************************************************************************************************************

    private getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    //******************************************************************************************************************
    //  CLONE
    //******************************************************************************************************************

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return ParticleElement
     */
    clone(): ParticleElement {
        let element: ParticleElement = new ParticleElement();
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;
        element.visible = this.visible;
        element._target = this._target;
        element._blendMode = this._blendMode;
        element._emitOnce = this._emitOnce;
        element._preGenerate = this._preGenerate;
        let node: Element = document.createElement(this.name);
        node.setAttribute("Style2", this._styleName);
        element.create(node);
        element._markAsCloned(); // must be called last before return!
        return element;
    }
}

export const ParticleElementAttributes = {
    /**
     * Blend mode for animation.
     * Bleding modes:
     *   0 = NORMAL;
     *   1 = ADD;
     *   2 = MULTIPLY;
     *   3 = SCREEN;
     *   4 = OVERLAY;
     *   5 = DARKEN;
     *   6 = LIGHTEN;
     *   7 = COLOR_DODGE;
     *   8 = COLOR_BURN;
     *   9 = HARD_LIGHT;
     *   10 = SOFT_LIGHT;
     *   11 = DIFFERENCE;
     *   12 = EXCLUSION;
     *   13 = HUE;
     *   14 = SATURATION;
     *   15 = COLOR;
     *   16 = LUMINOSITY;
     * @property BlendMode
     * @type number
     * @default null
     */
    BLEND_MODE: "BlendMode",
    DEBUG: "Debug",
    /**
     * Random start frame, when animation particles are used
     */
    RANDOM_START_FRAME: "RandomStartFrame",
    SPEED: "Speed",
    EMIT_ONCE: "EmitOnce",
    START_TYPE: "StartType",
    /**
     * Setup particle position and tween after create, so Start function just restart already created tween
     */
    PRE_GENERATE: "PreGenerate",
    /**
     * How many particles in one fire will be used in percentage. Max 100%
     */
    MAX_EMIT: "MaxEmit",
    // Tweens used in BaseElementParticle
    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",

    POSITION_TWEEN_X: "PositionTweenX",
    POSITION_TWEEN_Y: "PositionTweenY",
    POSITION_TWEEN_EASE: "PositionTweenEase",
    POSITION_TWEEN_DURATION: "PositionTweenDuration",
    POSITION_TWEEN_POSITION: "PositionTweenPosition",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
    },

    ROTATION_TWEEN: "RotationTween",
    ROTATION_TWEEN_EASE: "RotationTweenEase",
    ROTATION_TWEEN_DURATION: "RotationTweenDuration",
    ROTATION_TWEEN_DELAY: "RotationTweenDelay",

    SCALE_TWEEN: "ScaleTween",
    SCALE_TWEEN_EASE: "ScaleTweenEase",
    SCALE_TWEEN_DURATION: "ScaleTweenDuration",
    /**
     * Style define animation. You can use one style on more than one place.
     * Style for ParticleElement has to be instance of ParticleStyle.
     * @property Style
     * @type string
     * @default null
     */
    STYLE: "Style",
    STYLE2: "Style2",
    EXTEND_STYLE: "ExtendStyle",
    START: "Start",
    STOPPING: "Stopping",
    STOP: "Stop",
    RESET: "Reset",
    CLEAR: "Clear",
    IMAGE: "Image",
    /**
     * Tint of animation. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",
    ATTR_COUNT: "Count",
    ATTR_TIME: "Time",
    ATTR_MIN: "Min",
    ATTR_MAX: "Max",
    ATTR_FROM_FRAME: "FromFrame",
    ATTR_IMAGES: "Images",
    ATTR_TYPE: "Type",
    ATTR_X: "X",
    ATTR_Y: "Y",
    ATTR_RANDOM_X: "RandomX",
    ATTR_RANDOM_Y: "RandomY",
    ATTR_WIDTH: "Width",
    ATTR_HEIGHT: "Height",
    ATTR_RADIUS: "Radius",
    ATTR_POSITION_RADIUS: "PositionRadius",
    ATTR_RANDOM_POSITION_ANGLE: "RandomPositionAngle",
    ATTR_MIN_RADIUS: "MinRadius",
    ATTR_INNER_RADIUS: "InnerRadius",
    ATTR_ANGLE: "Angle",
    ATTR_SHOW_ANGLE: "ShowAngle",
    ATTR_VALUES: "Values",
    ATTR_VALUE: "Value",
    IMAGE_MASK: "ImageMask",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/ParticleElement.ts
 **/